import React, { useState } from 'react';
import { Check, Sparkles, ArrowRight, Shield, Package } from 'lucide-react';
import { Button } from '../ui/Button';
import { AnimatedSection } from './AnimatedSection';
import { useCart } from '../../contexts/CartContext';
import { pricingCards, cardAccents } from './homeData';
interface PricingSectionProps {
  onNavigate: (page: string) => void;
}
const packages = [{
  id: 'pkg-20',
  title: '20 отчётов',
  desc: 'Оптимально для регулярного использования',
  price: 76000,
  perReport: '3 800 ₽',
  savings: '14 000 ₽',
  savingsPercent: '~15%',
  features: ['Любые отчёты на выбор', 'Использование в любое время', 'Доступ без ограничений по времени'],
  popular: false
}, {
  id: 'pkg-50',
  title: '50 отчётов',
  desc: 'Лучший выбор для активных пользователей',
  price: 180000,
  perReport: '3 600 ₽',
  savings: '45 000 ₽',
  savingsPercent: '~20%',
  features: ['Любые отчёты', 'Быстрое масштабирование работы', 'Приоритетная обработка'],
  popular: true
}, {
  id: 'pkg-100',
  title: '100 отчётов',
  desc: 'Максимальная выгода для профессионалов',
  price: 315000,
  perReport: '3 150 ₽',
  savings: '135 000 ₽',
  savingsPercent: '~30%',
  features: ['Все типы отчётов', 'Максимальная экономия', 'Подходит для бизнеса'],
  popular: false
}];
export function PricingSection({
  onNavigate
}: PricingSectionProps) {
  const {
    addItem
  } = useCart();
  const [activeTab, setActiveTab] = useState<'single' | 'packages'>('single');
  const handleBuyPackage = (pkg: (typeof packages)[number]) => {
    addItem({
      id: `pkg-${pkg.id}-${Date.now()}`,
      name: pkg.title,
      description: pkg.desc,
      price: pkg.price,
      type: 'package',
      format: 'PDF-отчёты',
      duration: 'Бессрочно',
      badges: [{
        label: 'Пакет отчётов',
        variant: 'success'
      }]
    });
    onNavigate('checkout');
  };
  const formatPrice = (p: number) => p.toLocaleString('ru-RU') + ' ₽';
  return <AnimatedSection className="py-8 sm:py-16 bg-white" data-id="element-16478">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-16479">
        <div className="text-center mb-4 sm:mb-8" data-id="element-16480">
          <div className="inline-flex items-center px-3 sm:px-4 py-1 sm:py-1.5 rounded-full bg-slate-100 text-slate-600 text-[10px] sm:text-xs font-semibold uppercase tracking-[0.15em] mb-3 sm:mb-4" data-id="element-16481">
            <Shield className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1.5 sm:mr-2" data-id="element-16482" />
            Прозрачные цены
          </div>
          <h2 className="text-lg sm:text-3xl md:text-4xl font-bold text-slate-900 mb-2 sm:mb-4" data-id="element-16483">
            Наши услуги и цены
          </h2>
          <p className="text-xs sm:text-lg text-slate-500 max-w-xl mx-auto" data-id="element-16484">
            Простые цены без скрытых платежей. Гарантия возврата 7 дней.
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="flex justify-center mb-6 sm:mb-8" data-id="element-16485">
          <div className="inline-flex bg-slate-100 rounded-xl p-1 gap-1" data-id="element-16486">
            <button onClick={() => setActiveTab('single')} className={`px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-semibold transition-all ${activeTab === 'single' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-16487">
              Разовые
            </button>
            <button onClick={() => setActiveTab('packages')} className={`px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-semibold transition-all flex items-center gap-1.5 ${activeTab === 'packages' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-16488">
              <Package className="w-3.5 h-3.5" data-id="element-16489" />
              Пакеты
              <span className="text-[9px] sm:text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full leading-none" data-id="element-16490">
                до -30%
              </span>
            </button>
          </div>
        </div>

        {/* Single Pricing Tab */}
        {activeTab === 'single' && <div data-id="element-16491">
            <div className="relative mt-2" data-id="element-16492">
              <div className="xl:hidden absolute left-0 top-0 bottom-0 w-4 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" data-id="element-16493" />
              <div className="xl:hidden absolute right-0 top-0 bottom-0 w-4 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" data-id="element-16494" />
              <div className="flex gap-3 overflow-x-auto xl:overflow-x-visible pb-4 xl:pb-0 snap-x snap-mandatory xl:snap-none -mx-1 px-1 xl:mx-0 xl:px-0 xl:grid xl:grid-cols-5" style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }} data-id="element-16495">
                {pricingCards.map((card, idx) => {
              const accent = cardAccents[idx];
              if (card.popular) {
                return <div key={idx} className="flex-shrink-0 w-[240px] xl:w-auto snap-center pt-4" data-id="element-16496">
                        <div className="relative h-full" data-id="element-16497">
                          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10" data-id="element-16498">
                            <span className="bg-white text-indigo-600 text-[11px] font-bold px-3 py-1 rounded-full shadow-lg flex items-center gap-1 whitespace-nowrap" data-id="element-16499">
                              <Sparkles className="w-3 h-3" data-id="element-16500" />
                              Популярно
                            </span>
                          </div>
                          <div className="bg-gradient-to-br from-indigo-600 via-violet-600 to-purple-600 rounded-2xl p-4 xl:p-5 flex flex-col h-full shadow-xl shadow-indigo-500/20" data-id="element-16501">
                            <div className={`w-10 h-10 rounded-lg ${accent.iconBg} flex items-center justify-center mb-3 mt-1`} data-id="element-16502">
                              {accent.icon}
                            </div>
                            <h3 className="text-[15px] font-bold text-white mb-1 leading-tight" data-id="element-16503">
                              {card.title}
                            </h3>
                            <p className="text-indigo-200 text-xs mb-3 flex-grow leading-relaxed" data-id="element-16504">
                              {card.desc}
                            </p>
                            <ul className="space-y-1.5 mb-4 text-xs text-indigo-100" data-id="element-16505">
                              {card.features.map((f, i) => <li key={i} className="flex items-center" data-id="element-16506">
                                  <div className={`w-3.5 h-3.5 rounded-full ${accent.checkBg} flex items-center justify-center mr-1.5 flex-shrink-0`} data-id="element-16507">
                                    <Check className={`w-2 h-2 ${accent.checkText}`} data-id="element-16508" />
                                  </div>
                                  {f}
                                </li>)}
                            </ul>
                            <div className="flex items-baseline gap-1.5 mb-0.5" data-id="element-16509">
                              <span className="text-xl font-bold text-white" data-id="element-16510">
                                {card.price}
                              </span>
                              <span className="text-xs text-indigo-200 line-through" data-id="element-16511">
                                {card.oldPrice}
                              </span>
                            </div>
                            <span className="text-[11px] font-semibold text-emerald-300 mb-3" data-id="element-16512">
                              Выгода 17%
                            </span>
                            <Button fullWidth variant="outline" className="!bg-white !text-indigo-600 !border-white/30 hover:!bg-indigo-50 text-sm py-2" onClick={() => onNavigate(card.route)} data-id="element-16513">
                              Выбрать
                            </Button>
                          </div>
                        </div>
                      </div>;
              }
              return <div key={idx} className="flex-shrink-0 w-[240px] xl:w-auto snap-center pt-4" data-id="element-16514">
                      <div className={`relative bg-gradient-to-b ${accent.tint} to-white rounded-2xl border border-slate-200/80 border-t-2 ${accent.border} p-4 xl:p-5 h-full flex flex-col`} data-id="element-16515">
                        <div className={`w-10 h-10 rounded-lg ${accent.iconBg} flex items-center justify-center mb-3`} data-id="element-16516">
                          {accent.icon}
                        </div>
                        <h3 className="text-[15px] font-bold text-slate-900 mb-1 leading-tight" data-id="element-16517">
                          {card.title}
                        </h3>
                        <p className="text-slate-500 text-xs mb-3 flex-grow leading-relaxed" data-id="element-16518">
                          {card.desc}
                        </p>
                        <ul className="space-y-1.5 mb-4 text-xs text-slate-600" data-id="element-16519">
                          {card.features.map((f, i) => <li key={i} className="flex items-center" data-id="element-16520">
                              <div className={`w-3.5 h-3.5 rounded-full ${accent.checkBg} flex items-center justify-center mr-1.5 flex-shrink-0`} data-id="element-16521">
                                <Check className={`w-2 h-2 ${accent.checkText}`} data-id="element-16522" />
                              </div>
                              {f}
                            </li>)}
                        </ul>
                        <div className="text-xl font-bold text-slate-900 mb-3" data-id="element-16523">
                          {card.price}
                        </div>
                        <Button fullWidth variant="outline" className="text-sm py-2" onClick={() => onNavigate(card.route)} data-id="element-16524">
                          Выбрать
                        </Button>
                      </div>
                    </div>;
            })}
              </div>
            </div>
            <div className="text-center mt-5" data-id="element-16525">
              <Button variant="ghost" className="text-slate-500" onClick={() => onNavigate('pricing')} data-id="element-16526">
                Все цены и услуги <ArrowRight className="ml-2 w-4 h-4" data-id="element-16527" />
              </Button>
            </div>
          </div>}

        {/* Packages Tab */}
        {activeTab === 'packages' && <div data-id="element-16528">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mt-2" data-id="element-16529">
              {packages.map(pkg => {
            if (pkg.popular) {
              return <div key={pkg.id} className="relative pt-4" data-id="element-16530">
                      <div className="absolute -top-0 left-1/2 transform -translate-x-1/2 z-10" data-id="element-16531">
                        <span className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-[11px] font-bold px-4 py-1 rounded-full shadow-lg flex items-center gap-1.5 whitespace-nowrap" data-id="element-16532">
                          <Sparkles className="w-3 h-3" data-id="element-16533" />
                          Популярно
                        </span>
                      </div>
                      <div className="bg-gradient-to-br from-indigo-600 via-violet-600 to-purple-600 rounded-2xl p-5 sm:p-6 flex flex-col h-full shadow-xl shadow-indigo-500/20" data-id="element-16534">
                        <h3 className="text-lg sm:text-xl font-bold text-white mb-1 mt-2" data-id="element-16535">
                          {pkg.title}
                        </h3>
                        <p className="text-indigo-200 text-xs sm:text-sm mb-4" data-id="element-16536">
                          {pkg.desc}
                        </p>

                        <div className="text-2xl sm:text-3xl font-bold text-white mb-1" data-id="element-16537">
                          {formatPrice(pkg.price)}
                        </div>
                        <div className="flex items-center gap-3 mb-4 text-xs sm:text-sm" data-id="element-16538">
                          <span className="text-indigo-200" data-id="element-16539">
                            {pkg.perReport} за отчёт
                          </span>
                          <span className="font-bold text-emerald-300" data-id="element-16540">
                            −{pkg.savings} ({pkg.savingsPercent})
                          </span>
                        </div>

                        <ul className="space-y-2 mb-5 flex-grow" data-id="element-16541">
                          {pkg.features.map((f, i) => <li key={i} className="flex items-center text-xs sm:text-sm text-indigo-100" data-id="element-16542">
                              <div className="w-4 h-4 rounded-full bg-white/20 flex items-center justify-center mr-2 flex-shrink-0" data-id="element-16543">
                                <Check className="w-2.5 h-2.5 text-white" data-id="element-16544" />
                              </div>
                              {f}
                            </li>)}
                        </ul>

                        <Button fullWidth variant="outline" className="!bg-white !text-indigo-600 !border-white/30 hover:!bg-indigo-50 text-sm py-2.5" onClick={() => handleBuyPackage(pkg)} data-id="element-16545">
                          Купить пакет
                        </Button>
                      </div>
                    </div>;
            }
            return <div key={pkg.id} className="pt-4" data-id="element-16546">
                    <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6 flex flex-col h-full hover:shadow-lg transition-shadow" data-id="element-16547">
                      <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-1" data-id="element-16548">
                        {pkg.title}
                      </h3>
                      <p className="text-slate-500 text-xs sm:text-sm mb-4" data-id="element-16549">
                        {pkg.desc}
                      </p>

                      <div className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1" data-id="element-16550">
                        {formatPrice(pkg.price)}
                      </div>
                      <div className="flex items-center gap-3 mb-4 text-xs sm:text-sm" data-id="element-16551">
                        <span className="text-slate-500" data-id="element-16552">
                          {pkg.perReport} за отчёт
                        </span>
                        <span className="font-bold text-emerald-600" data-id="element-16553">
                          −{pkg.savings} ({pkg.savingsPercent})
                        </span>
                      </div>

                      <ul className="space-y-2 mb-5 flex-grow" data-id="element-16554">
                        {pkg.features.map((f, i) => <li key={i} className="flex items-center text-xs sm:text-sm text-slate-700" data-id="element-16555">
                            <div className="w-4 h-4 rounded-full bg-emerald-50 flex items-center justify-center mr-2 flex-shrink-0" data-id="element-16556">
                              <Check className="w-2.5 h-2.5 text-emerald-500" data-id="element-16557" />
                            </div>
                            {f}
                          </li>)}
                      </ul>

                      <Button fullWidth variant="outline" className="text-sm py-2.5" onClick={() => handleBuyPackage(pkg)} data-id="element-16558">
                        Купить пакет
                      </Button>
                    </div>
                  </div>;
          })}
            </div>

            <div className="text-center mt-6" data-id="element-16559">
              <p className="text-xs sm:text-sm text-slate-500 font-medium" data-id="element-16560">
                Отчёты из пакета списываются автоматически при покупке услуг
              </p>
            </div>

            <div className="text-center mt-4" data-id="element-16561">
              <Button variant="ghost" className="text-slate-500" onClick={() => onNavigate('pricing')} data-id="element-16562">
                Все цены и услуги <ArrowRight className="ml-2 w-4 h-4" data-id="element-16563" />
              </Button>
            </div>
          </div>}
      </div>
    </AnimatedSection>;
}